import { useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import { publicRequest, userRequesIntegration } from "../../requestMethods";
import {
  buildProductRequest,
  describeRequest,
  filterMireCompanies,
  friendlyError,
  summarizeConsulta,
} from "../../util/mireProduct";
import "./mireIntegration.css";

const REQUEST_TIMEOUT_MS = 60 * 1000;

export default function MireIntegration() {
  const [companies, setCompanies] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(true);
  const [companyId, setCompanyId] = useState("");
  const [productCode, setProductCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    const getCompanies = async () => {
      try {
        const res = await publicRequest.get(`/company`);
        setCompanies(filterMireCompanies(res.data));
      } catch (err) {
        console.error("Erro ao buscar empresas:", err);
        setCompanies([]);
      } finally {
        setLoadingCompanies(false);
      }
    };
    getCompanies();
  }, []);

  const company = useMemo(
    () => companies.find((c) => String(c.companyId) === String(companyId)),
    [companies, companyId]
  );

  const consultar = async () => {
    if (loading) return;

    const built = buildProductRequest({ companyId, productCode });
    if (!built.ok) {
      Swal.fire("Falta um dado", built.msg, "warning");
      return;
    }

    setResult(null);
    setLoading(true);
    try {
      const res = await userRequesIntegration.get(built.request.url, {
        timeout: REQUEST_TIMEOUT_MS,
      });
      const resumo = summarizeConsulta(res.data);
      setResult({
        status: resumo.found ? "ok" : "warn",
        ...resumo,
        request: describeRequest(built.request),
        raw: res.data,
      });
    } catch (err) {
      setResult({
        status: "fail",
        found: false,
        message: friendlyError(err),
        products: [],
        errors: [],
        request: describeRequest(built.request),
        raw: (err.response && err.response.data) || err.message,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mire_page">
      <div className="mire_header">
        <span className="mire_title">Consulta de produto na Mire</span>
        <span className="mire_subtitle">
          Mostra como o produto está <b>agora</b> na Mire. Compare com o admin: se
          aqui já aparece o dado novo, o problema é na importação; se aqui ainda
          está o dado antigo, a Mire é que não enviou.
        </span>
      </div>

      <div className="mire_step">
        <div className="mire_step_title">
          <span className="mire_step_number">1</span> Escolha a empresa
        </div>
        {loadingCompanies ? (
          <span className="mire_hint">Carregando empresas...</span>
        ) : companies.length === 0 ? (
          <span className="mire_empty">
            Nenhuma empresa com integração Mire encontrada. Verifique o cadastro da
            empresa (campo "Url integração externa" deve ser <b>mire</b>).
          </span>
        ) : (
          <>
            <select
              className="mire_select"
              value={companyId}
              disabled={loading}
              onChange={(e) => setCompanyId(e.target.value)}
            >
              <option value="">Selecione a empresa</option>
              {companies.map((c) => (
                <option key={c.companyId} value={c.companyId}>
                  {c.name}
                </option>
              ))}
            </select>
            {company && (
              <span className="mire_hint">
                Código da empresa: {company.companyId}
                {company.slug ? ` · ${company.slug}` : ""}
              </span>
            )}
          </>
        )}
      </div>

      <div className="mire_step">
        <div className="mire_step_title">
          <span className="mire_step_number">2</span> Código do produto na Mire
        </div>
        <div className="mire_run">
          <input
            className="mire_input"
            type="text"
            value={productCode}
            disabled={loading}
            placeholder="Ex.: 240158020"
            onChange={(e) => setProductCode(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && consultar()}
          />
          <button
            type="button"
            className="mire_run_button"
            disabled={loading || !companyId}
            onClick={consultar}
          >
            {loading ? "Consultando..." : "Consultar"}
          </button>
          {loading && <div className="mire_spinner" />}
        </div>
        <span className="mire_hint">
          É o mesmo código que aparece como <b>Referência</b> do produto no admin.
        </span>
      </div>

      {result && <ConsultaResultado result={result} />}
    </div>
  );
}

function ConsultaResultado({ result }) {
  return (
    <>
      <div className={`mire_result ${result.status}`}>
        <div className="mire_result_msg">{result.message}</div>
        <details className="mire_details">
          <summary>Detalhes técnicos (para enviar ao time de tecnologia)</summary>
          <pre>
            {result.request}
            {"\n\n"}
            {typeof result.raw === "string"
              ? result.raw
              : JSON.stringify(result.raw, null, 2)}
          </pre>
        </details>
      </div>

      {result.products.map((p, i) => (
        <ProdutoCard key={`${p.code}-${p.store || i}`} produto={p} />
      ))}
    </>
  );
}

function ProdutoCard({ produto }) {
  return (
    <div className="mire_card">
      <div className="mire_card_head">
        <div>
          <span className="mire_card_title">{produto.title}</span>
          <span className="mire_card_code">
            Código {produto.code}
            {produto.store ? ` · Loja ${produto.store}` : ""}
          </span>
        </div>
        <div className="mire_updated">
          <span className="mire_updated_label">Atualizado na Mire em</span>
          <span className="mire_updated_value">{produto.updatedAt}</span>
          {produto.updatedAge && (
            <span className="mire_updated_age">{produto.updatedAge}</span>
          )}
        </div>
      </div>

      <div className="mire_block">
        <div className="mire_block_title">Preços</div>
        <div className="mire_prices">
          {produto.prices.map((p) => (
            <div key={p.key} className={`mire_price ${p.used ? "used" : ""}`}>
              <span className="mire_price_label">
                {p.label}
                {p.used ? " · usado nesta loja" : ""}
              </span>
              <span className="mire_price_value">{p.value}</span>
              {p.promotion && (
                <span className="mire_price_promo">Promoção: {p.promotion}</span>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="mire_block">
        <div className="mire_block_title">
          Grade e estoque
          <span className="mire_block_total">
            Total: {produto.grid.totalStock.toLocaleString("pt-BR")} peças
          </span>
        </div>
        {produto.grid.rows.length === 0 ? (
          <span className="mire_hint">
            A Mire não devolveu nenhuma variação para este produto.
          </span>
        ) : (
          <table className="mire_table">
            <thead>
              <tr>
                <th>Cor</th>
                <th>Tamanho</th>
                <th>Estoque</th>
                <th>SKU</th>
                <th>Loja</th>
              </tr>
            </thead>
            <tbody>
              {produto.grid.rows.map((r) => (
                <tr key={r.sku} className={r.stock <= 0 ? "zerado" : ""}>
                  <td>{r.color}</td>
                  <td>{r.size}</td>
                  <td className="num">{r.stockLabel}</td>
                  <td className="mono">{r.sku}</td>
                  <td>{r.storeId ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="mire_block">
        <div className="mire_block_title">
          Fotos
          <span className="mire_block_total">
            {produto.images.reduce((acc, g) => acc + g.urls.length, 0)} foto(s)
          </span>
        </div>
        {produto.images.length === 0 ? (
          <span className="mire_hint">
            A Mire não devolveu nenhuma foto para este produto.
          </span>
        ) : (
          produto.images.map((g) => (
            <div key={g.color} className="mire_image_group">
              <span className="mire_image_color">{g.color}</span>
              <div className="mire_thumbs">
                {g.urls.map((url) => (
                  <a key={url} href={url} target="_blank" rel="noreferrer">
                    <img className="mire_thumb" src={url} alt={g.color} />
                  </a>
                ))}
              </div>
            </div>
          ))
        )}
      </div>

      <div className="mire_block">
        <div className="mire_block_title">Cadastro</div>
        <div className="mire_details_grid">
          {produto.details.map((d) => (
            <div key={d.label} className="mire_detail">
              <span className="mire_detail_label">{d.label}</span>
              <span className="mire_detail_value">{d.value}</span>
            </div>
          ))}
          <div className="mire_detail">
            <span className="mire_detail_label">Cadastrado em</span>
            <span className="mire_detail_value">{produto.createdAt}</span>
          </div>
        </div>
        <div className="mire_description">
          <span className="mire_detail_label">Descrição completa</span>
          <p>{produto.description}</p>
        </div>
      </div>
    </div>
  );
}
